import type {ReactElement} from 'react';
import React from 'react';
import {SubtitlesEditor} from '@Components/poster-editor/components/poster-editor-subtitles-editor/components/subtitles-editor/subtitles-editor';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import type {SubtitleObject} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import type {PosterEditorSubtitles} from './poster-editor-subtitles-editor.types';
import {useGetCurrentSubtitleObject} from '@Hooks/poster-editor/useGetCurrentSubtitleObject';

export function PosterEditorSubtitlesEditor(): ReactElement | null {
  const activeTranscriptItem = useGetSelectedTranscriptItem();
  const currentSubtitle = useGetCurrentSubtitleObject();

  if (activeTranscriptItem === null) {
    return null;
  }

  const onSubtitlesChange = async (subtitles: PosterEditorSubtitles, isUndoable = true, updateRedux = true): Promise<void> => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }

    const subtitlesDataHashmap = subtitles.subtitlesHashmap;

    await activeTranscriptItem.updateFromObject(
      {
        subtitlesHashmap: subtitlesDataHashmap,
      },
      {undoable: isUndoable, updateRedux}
    );

    currentPage.calculateAndUpdatePageDuration({updateRedux: true});
  };

  const seekToSubtitle = (selectedId: string): void => {
    const pageStartTimeforTranscriptItem = activeTranscriptItem.getPageStartTime();

    const totalSeekTime = Math.round((pageStartTimeforTranscriptItem + activeTranscriptItem.subtitlesHashmap[selectedId].startTime) * 1000) / 1000;

    void activeTranscriptItem.page.poster.seek(totalSeekTime);
  };

  const getSubtitleObjectsHashmapFromActiveTranscriptItem = (): Record<string, SubtitleObject> => {
    const activeTranscriptItemSubtitlesHashmap = {...activeTranscriptItem.subtitlesHashmap};
    const subtitleObjectsHashmap: Record<string, SubtitleObject> = {};

    Object.entries(activeTranscriptItemSubtitlesHashmap).forEach(([uid, subtitle]) => {
      subtitleObjectsHashmap[uid] = {
        ...subtitle.toObject(),
      };
    });

    return subtitleObjectsHashmap;
  };

  const getSubtitlesData = (): PosterEditorSubtitles => {
    return {
      subtitlesOrder: activeTranscriptItem.getSubtitleIdsInOrder(),
      subtitlesHashmap: getSubtitleObjectsHashmapFromActiveTranscriptItem(),
    };
  };

  return (
    <SubtitlesEditor
      selectedSubtitleId={currentSubtitle?.subtitleUID}
      setSelectedSubtitleId={seekToSubtitle}
      isMobileVariant={isEditorMobileVariant()}
      subtitles={getSubtitlesData()}
      onSubtitlesChange={onSubtitlesChange}
    />
  );
}
